import { useCallback } from "react";
import { useListaCEPApi } from "../../../infra/geo-veles/lista-cep";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../presentation/hooks/store-hooks";
import { useModalContainerService } from "../../modal-container";
import { useUIService } from "../../ui";
import { cepNumerico, getDistinctValues, validateCep } from "../../../utils";
import {
  resetCreateListaCEPFormState,
  resetEditListaCEPFormState,
  resetListaCEPFileDataState,
  resetListasCEPState,
  setCEPsEditListaCEPState,
  setCreateListaCEPFormErrorsState,
  setCreateListaCEPFormTouchedState,
  setCreateListaCEPIdUsuarioState,
  setCreateListaCEPNomeState,
  setEditListaCEPAddCEPState,
  setEditListaCEPAtivaState,
  setEditListaCEPEditCEPState,
  setEditListaCEPFormErrorsState,
  setEditListaCEPFormTouchedState,
  setEditListaCEPIdVelesGeoListaCEPState,
  setEditListaCEPNomeState,
  setEditListaCEPRemoveCEPState,
  setListaCEPDistinctValuesState,
  setListaCEPFileDataListState,
  setListaCEPFileDataState,
  setListaCEPFileInvalidDataListState,
  setListaCEPFileRepeatedDataListState,
  setListaCEPFileValidDataListState,
  setListaCEPSelectedColumnState,
  setListaCEPValidValuesState,
  setListasCEPState,
  setOpenCreateListaCEPState,
  setOpenEditCEPState,
  setOpenEditListaCEPState,
  setOpenImportFileCEPState,
  setOpenPreviewListaCEPState,
  setSelectedListasCEPState,
  validateCreateListaCEPForm,
  validateEditListaCEPForm,
} from "../../../application/states/geo-veles";
import { Box, Button, Grid, Typography } from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { IListaCEP } from "../../../application/models";

export const useListaCEPService = () => {
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const {
    user: { currentUser },
    listaCEP,
  } = useAppSelector((state) => state);
  // Hook
  const { openModal, closeModal } = useModalContainerService();
  const { setLoading, setAlert } = useUIService();
  // API
  const {
    createListaCEPApi,
    findListasCEPApi,
    updateListaCEPApi,
    updateListaCEPStatusApi,
  } = useListaCEPApi();

  const findCepKeyInArray = useCallback((array: any[]) => {
    for (let i = 0; i < array.length; i++) {
      const keys = Object.keys(array[i]);
      for (let k = 0; k < keys.length; k++) {
        if (validateCep(cepNumerico(`${array[i][keys[k]]}`))) {
          return keys[k];
        }
      }
    }
    return null;
  }, []);

  const resetListaCEPFileData = useCallback(() => {
    dispatch(resetListaCEPFileDataState());
  }, [dispatch]);

  const selectColumn = useCallback(
    (data: { selectedColumn: string; data?: any[] }) => {
      const loadingId = Math.random().toString();

      setLoading({
        id: loadingId,
        loading: true,
        message: "Selecionando coluna...",
      });

      dispatch(
        setListaCEPSelectedColumnState({
          selectedColumn: data.selectedColumn,
        })
      );
      /* performatic code to get distinct values */
      let dataToUse = listaCEP?.fileData?.data ?? [];
      if (data.data) {
        dataToUse = data.data;
      }

      const distinctValues = getDistinctValues(
        dataToUse.map((item: any) => item[data.selectedColumn])
      );

      dispatch(
        setListaCEPDistinctValuesState({
          distinctValues: distinctValues.length,
        })
      );

      const filteredList = [...distinctValues].filter((item) => {
        const replacedItem = cepNumerico(String(item));
        return !listaCEP.editListaCEPForm.fields.ceps.includes(replacedItem);
      });

      const distinctValidValues = [...distinctValues].filter((value: any) => {
        let isValid = validateCep(cepNumerico(value));

        if (isValid) {
          return true;
        } else {
          return false;
        }
      });

      const repeatedList = [...distinctValues].filter((item) => {
        const replacedItem = cepNumerico(item);
        return listaCEP.editListaCEPForm.fields.ceps.includes(replacedItem);
      });

      const validValues = [...filteredList].filter((value: any) => {
        let isValid = validateCep(cepNumerico(value));
        if (isValid) {
          return true;
        } else {
          return false;
        }
      });

      const invalidValues = [...filteredList].filter((value: any) => {
        let isValid = validateCep(cepNumerico(value));
        if (!isValid) {
          return true;
        } else {
          return false;
        }
      });

      dispatch(
        setListaCEPValidValuesState({
          validValues: distinctValidValues.length,
        })
      );
      dispatch(
        setListaCEPFileDataListState({
          value: validValues.map((cep) => cepNumerico(cep)),
        })
      );
      dispatch(
        setListaCEPFileValidDataListState({
          value: validValues.map((cep) => cepNumerico(cep)),
        })
      );
      dispatch(
        setListaCEPFileInvalidDataListState({
          value: invalidValues.map((cep) => cepNumerico(cep)),
        })
      );
      dispatch(
        setListaCEPFileRepeatedDataListState({
          value: repeatedList.map((cep) => cepNumerico(cep)),
        })
      );

      setLoading({
        id: loadingId,
        loading: false,
      });
    },
    [
      dispatch,
      listaCEP.editListaCEPForm.fields.ceps,
      listaCEP.fileData.data,
      setLoading,
    ]
  );

  const setFile = useCallback(
    (data: {
      name: string;
      size: string;
      columns: string[];
      type: string;
      data: any[];
    }) => {
      const loadingId = Math.random().toString();

      setLoading({
        id: loadingId,
        loading: true,
        message: "Lendo arquivo...",
      });

      dispatch(
        setListaCEPFileDataState({
          fileData: {
            columns: data.columns,
            data: data.data,
            name: data.name,
            size: data.size,
            type: data.type,
          },
        })
      );

      const cepKey = findCepKeyInArray(data.data);

      if (cepKey) {
        const replacedFileData = [];
        for (let i = 0; i < data.data.length; i++) {
          const cep = data.data[i];

          if (typeof cep[cepKey] === "number") {
            const replacedCnpj = cepNumerico(String(cep[cepKey]) as string);

            replacedFileData.push(replacedCnpj);
          }
          if (typeof cep[cepKey] === "string") {
            const replacedCnpj = cepNumerico(String(cep[cepKey]));

            replacedFileData.push(replacedCnpj);
          }
        }

        const filteredList = replacedFileData.filter((item) => {
          return !listaCEP.editListaCEPForm.fields.ceps.includes(item);
        });

        const repeatedList = replacedFileData.filter((item) => {
          const replacedItem = cepNumerico(item);
          return listaCEP.editListaCEPForm.fields.ceps.includes(replacedItem);
        });

        const validValues = filteredList.filter((value: string) => {
          const numericCep = cepNumerico(value);

          let isValid = validateCep(numericCep);
          if (isValid) {
            return numericCep;
          } else {
            return false;
          }
        });

        if (validValues.length < 1) {
          if (repeatedList.length > 0) {
            setLoading({
              id: loadingId,
              loading: false,
            });

            openModal({
              content: (
                <>
                  <Box p={3}>
                    <Grid
                      container
                      alignItems={"center"}
                      flexDirection="column"
                    >
                      <Grid item>
                        <ErrorIcon color="secondary" sx={{ fontSize: 100 }} />
                      </Grid>
                      <Grid item>
                        <Typography
                          variant="h4"
                          sx={{ mt: 2 }}
                          align="center"
                          textTransform="uppercase"
                        >
                          Encontramos apenas CEPs já inseridos na sua lista
                          atual.
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Typography
                          variant="body1"
                          sx={{ mt: 2 }}
                          align="center"
                        >
                          Não encontramos nenhuma coluna com CEPs válidos e
                          novos no arquivo.Selecione um arquivo com CEPs
                          válidos.
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Box my={3}>
                          <Button
                            variant="contained"
                            onClick={() => {
                              closeModal();
                              resetListaCEPFileData();
                            }}
                          >
                            Ok
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </>
              ),
            });
          } else {
            setLoading({
              id: loadingId,
              loading: false,
            });

            openModal({
              content: (
                <>
                  <Box p={3}>
                    <Grid
                      container
                      alignItems={"center"}
                      flexDirection="column"
                    >
                      <Grid item>
                        <ErrorIcon color="secondary" sx={{ fontSize: 100 }} />
                      </Grid>
                      <Grid item>
                        <Typography
                          variant="h4"
                          sx={{ mt: 2 }}
                          align="center"
                          textTransform="uppercase"
                        >
                          Não encontramos CEPs válidos no seu arquivo
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Typography
                          variant="body1"
                          sx={{ mt: 2 }}
                          align="center"
                        >
                          Não encontramos nenhuma coluna com CEPs válidos e
                          novos. Selecione outro arquivo.
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Box my={3}>
                          <Button
                            variant="contained"
                            onClick={() => {
                              closeModal();
                              resetListaCEPFileData();
                            }}
                          >
                            Ok
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </>
              ),
            });
          }
        } else {
          setLoading({
            id: loadingId,
            loading: false,
          });

          selectColumn({
            selectedColumn: cepKey,
            data: data.data,
          });

          openModal({
            content: (
              <>
                <Box p={3}>
                  <Grid container alignItems={"center"} flexDirection="column">
                    <Grid item>
                      <CheckCircleIcon color="primary" sx={{ fontSize: 100 }} />
                    </Grid>
                    <Grid item>
                      <Typography
                        variant="h4"
                        sx={{ mt: 2 }}
                        align="center"
                        textTransform="uppercase"
                      >
                        Arquivo <strong> {data.name} </strong> carregado com
                        sucesso!
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Typography variant="body1" sx={{ mt: 2 }} align="center">
                        Selecionamos a coluna <strong> {cepKey} </strong> como a
                        coluna que contém os CEPs. Caso esta não seja a coluna
                        correta, selecione a correta no menu indicado.
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Box my={3}>
                        <Button
                          variant="contained"
                          onClick={() => {
                            closeModal();
                          }}
                        >
                          Ok
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </>
            ),
          });
        }
      } else {
        setLoading({
          id: loadingId,
          loading: false,
        });

        openModal({
          content: (
            <>
              <Box p={3}>
                <Grid container alignItems={"center"} flexDirection="column">
                  <Grid item>
                    <ErrorIcon color="secondary" sx={{ fontSize: 100 }} />
                  </Grid>
                  <Grid item>
                    <Typography
                      variant="h4"
                      sx={{ mt: 2 }}
                      align="center"
                      textTransform="uppercase"
                    >
                      Não encontramos CEPs válidos no seu arquivo
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="body1" sx={{ mt: 2 }} align="center">
                      Não encontramos nenhuma coluna com CEPs válidos. Selecione
                      outro arquivo.
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Box my={3}>
                      <Button
                        variant="contained"
                        onClick={() => {
                          closeModal();
                          resetListaCEPFileData();
                        }}
                      >
                        Ok
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </>
          ),
        });
      }
    },
    [
      setLoading,
      dispatch,
      findCepKeyInArray,
      listaCEP.editListaCEPForm.fields.ceps,
      openModal,
      closeModal,
      resetListaCEPFileData,
      selectColumn,
    ]
  );

  const setCreateListaCEPName = useCallback(
    (name: string) => {
      dispatch(
        setCreateListaCEPNomeState({
          nome: name,
        })
      );
    },
    [dispatch]
  );

  const setEditListaCEPName = useCallback(
    (name: string) => {
      dispatch(
        setEditListaCEPNomeState({
          nome: name,
        })
      );
    },
    [dispatch]
  );

  const resetForm = useCallback(() => {
    dispatch(resetEditListaCEPFormState());
  }, [dispatch]);

  const addFileListToListaCEP = useCallback(() => {
    const loadingId = Math.random().toString();

    try {
      setLoading({
        id: loadingId,
        loading: true,
        message: "Lendo arquivo...",
      });

      /* Do not insert if there is no list selected */
      if (!listaCEP.editListaCEPForm.fields.ceps) {
        return;
      }
      /* Do not insert repeated values */
      const filteredList = [...listaCEP.fileListData.fileListData].filter(
        (item) => {
          return !listaCEP.editListaCEPForm.fields.ceps.includes(item);
        }
      );

      if (filteredList.length > 0) {
        dispatch(
          setCEPsEditListaCEPState({
            ceps: [
              ...listaCEP.editListaCEPForm.fields.ceps,
              ...listaCEP.fileListData.fileListTableValues.fileListValidData,
            ],
          })
        );

        setAlert({
          message: "Lista adicionada com sucesso!",
          type: "success",
        });
      }
    } catch (error) {
      setAlert({
        message: "Erro ao ler arquivo",
        type: "error",
      });
    } finally {
      setLoading({
        id: loadingId,
        loading: false,
      });
    }
  }, [
    setLoading,
    listaCEP.editListaCEPForm.fields.ceps,
    listaCEP.fileListData.fileListData,
    listaCEP.fileListData.fileListTableValues.fileListValidData,
    dispatch,
    setAlert,
  ]);

  const setEditListaCEPFields = useCallback(
    (data: { listaCEP: IListaCEP }) => {
      dispatch(
        setSelectedListasCEPState({
          listaCEP: data.listaCEP,
        })
      );

      dispatch(
        setEditListaCEPIdVelesGeoListaCEPState({
          idVelesGeoListaCEP: data.listaCEP.IdVelesGeoListaCEP,
        })
      );

      dispatch(
        setEditListaCEPNomeState({
          nome: data.listaCEP.Nome,
        })
      );

      dispatch(
        setEditListaCEPAtivaState({
          ativa: data.listaCEP.Ativa,
        })
      );

      dispatch(
        setCEPsEditListaCEPState({
          ceps: data.listaCEP.CEPs ?? [],
        })
      );
    },
    [dispatch]
  );

  const selectListaCEPToEdit = useCallback(
    (listaCEPId: number) => {
      const listaCEPToEdit = listaCEP.listasCEP.find(
        (item) => item.IdVelesGeoListaCEP === listaCEPId
      );
      if (listaCEPToEdit) {
        setEditListaCEPFields({
          listaCEP: listaCEPToEdit,
        });

        dispatch(setOpenEditListaCEPState(true));
      }
    },
    [dispatch, listaCEP.listasCEP, setEditListaCEPFields]
  );

  const updateListaCEP = useCallback(async () => {
    const loadingId = Math.random().toString();
    try {
      const errors = validateEditListaCEPForm(listaCEP.editListaCEPForm.fields);
      if (errors.length > 0) {
        dispatch(
          setEditListaCEPFormErrorsState({
            errors: errors,
          })
        );

        dispatch(
          setEditListaCEPFormTouchedState({
            touched: true,
          })
        );
      } else {
        setLoading({
          id: loadingId,
          loading: true,
          message: "Atualizando lista...",
        });
        await updateListaCEPApi({
          idVelesGeoListaCEP:
            listaCEP.editListaCEPForm.fields.idVelesGeoListaCEP,
          ceps: listaCEP.editListaCEPForm.fields.ceps,
          nome: listaCEP.editListaCEPForm.fields.nome,
        });

        setAlert({
          message: "Lista atualizada com sucesso",
          type: "success",
        });
        closeEditListaCEPForm();
        setupListaCEP()
      }
    } catch (error: any) {
      setAlert({
        message: error.response.data.error,
        type: "error",
      });
    } finally {
      setLoading({
        id: loadingId,
        loading: false,
      });
    }
  }, [
    listaCEP.editListaCEPForm.fields,
    dispatch,
    setLoading,
    updateListaCEPApi,
    setAlert,
  ]);

  const addItemToCEPList = useCallback(
    (item: string) => {
      const replacedCep = cepNumerico(item);

      if (!validateCep(replacedCep)) {
        setAlert({
          message: "CEP inválido",
          type: "error",
        });
      } else {
        if (listaCEP.editListaCEPForm.fields.ceps.includes(replacedCep)) {
          setAlert({
            message: "Este CEP já se encontra na lista",
            type: "error",
          });
        }

        if (!listaCEP.editListaCEPForm.fields.ceps.includes(replacedCep)) {          
          dispatch(
            setEditListaCEPAddCEPState({
              cep: replacedCep,
            })
          )         

          // Pequeno delay para aguardar o estado atualizar antes de chamar updateListaCEP
          setTimeout(() => {
            //updateListaCEP();

            setAlert({
              message: "CEP adicionado com sucesso",
              type: "success",
            });
          }, 50); // Delay mínimo suficiente
        }
      }
    },
    [dispatch, listaCEP.editListaCEPForm.fields.ceps, setAlert]
  );

  const listaCEPEditCEP = useCallback(
    (data: { cep: string; newCEP: string }) => {
      const novoCepPadronizado = cepNumerico(data.newCEP);

      if (validateCep(novoCepPadronizado)) {
        if (
          listaCEP.editListaCEPForm.fields.ceps.includes(novoCepPadronizado)
        ) {
          setAlert({
            message: "Este CEP já se encontra na lista atual",
            type: "error",
          });
        } else {
          dispatch(
            setEditListaCEPEditCEPState({
              oldCEP: data.cep,
              newCEP: novoCepPadronizado,
            })
          );

          setAlert({
            message: "CEP atualizado com sucesso",
            type: "success",
          });
        }
      } else {
        setAlert({
          message: "CEP inválido",
          type: "error",
        });
      }
    },
    [dispatch, listaCEP.editListaCEPForm.fields.ceps, setAlert]
  );

  const listaCEPRemoveCEP = useCallback(
    (cep: string) => {
      dispatch(
        setEditListaCEPRemoveCEPState({
          cep: cep,
        })
      );

      setAlert({
        message: "CEP removido com sucesso",
        type: "success",
      });
    },
    [dispatch, setAlert]
  );

  const setupListaCEP = useCallback(async () => {
    if (currentUser) {
      try {
        const getListasCEP = await findListasCEPApi({
          idUsuario: currentUser.IdUsuario,
        });

        dispatch(
          setListasCEPState({
            listasCEP: Array.isArray(getListasCEP) ? getListasCEP : [],
          })
        );
      } catch (error: any) {
        setAlert({
          message:
            error?.response?.data?.error ?? "Erro ao carregar listas de CEP",
          type: "error",
        });
      }
    }
  }, [currentUser, findListasCEPApi, dispatch, setAlert]);

  const createListaCEP = useCallback(async () => {
  if (currentUser) {
    const loadingId = Math.random().toString();
    try {
      const errors = validateCreateListaCEPForm(
        listaCEP.createListaCEPForm.fields
      );

      if (errors.length > 0) {
        dispatch(setCreateListaCEPFormErrorsState({ errors }));
        dispatch(setCreateListaCEPFormTouchedState({ touched: true }));
      } else {
        setLoading({
          id: loadingId,
          loading: true,
          message: "Criando lista...",
        });

        const novaListaCEP = await createListaCEPApi({
          idUsuario: currentUser.IdUsuario,
          nome: listaCEP.createListaCEPForm.fields.nome,
        });

        // 1. Atualiza a lista global
        dispatch(
          setListasCEPState({
            listasCEP: [...listaCEP.listasCEP, novaListaCEP],
          })
        );

        // 2. Fecha o formulário de criação
        dispatch(setOpenCreateListaCEPState(false));

        // 3. Preenche e abre o formulário de EDIÇÃO com a nova lista
        setEditListaCEPFields({ listaCEP: novaListaCEP });
        dispatch(setOpenEditListaCEPState(true));
        dispatch(resetCreateListaCEPFormState());

        setAlert({
          message: "Lista criada com sucesso",
          type: "success",
        });
      }
    } catch (error: any) {
      setAlert({
        message: error.response?.data?.error || "Erro ao criar lista de CEP",
        type: "error",
      });
    } finally {
      setLoading({
        id: loadingId,
        loading: false,
      });
    }
  }
}, [
  currentUser,
  listaCEP.createListaCEPForm.fields,
  listaCEP.listasCEP,
  dispatch,
  setLoading,
  createListaCEPApi,
  setAlert,
  setEditListaCEPFields, // ← não esqueça de adicionar nas dependências!
]);

  const updateListaCEPStatus = useCallback(
    async (data: { idVelesGeoListaCEP: number; status: boolean }) => {
      try {
        await updateListaCEPStatusApi({
          idVelesGeoListaCEP: data.idVelesGeoListaCEP,
          status: data.status,
        });

        if (data.status === false) {
          setAlert({
            message: "Lista excluída com sucesso",
            type: "success",
          });

          await setupListaCEP();
        }
      } catch (error: any) {
        setAlert({
          message: error.response.data.error,
          type: "error",
        });
      }
    },
    [setAlert, setupListaCEP, updateListaCEPStatusApi]
  );

  const verifyListaCEPFileData = useCallback(() => {
    const loadingId = Math.random().toString();

    setLoading({
      id: loadingId,
      loading: true,
      message: "Verificando se os CEPs do arquivo são válidos",
    });

    const validCeps =
      listaCEP.fileListData.fileListTableValues.fileListValidData;
    const invalidCeps =
      listaCEP.fileListData.fileListTableValues.fileListInvalidData;

    if (validCeps.length < 1 && invalidCeps.length > 0) {
      setLoading({
        id: loadingId,
        loading: false,
      });

      openModal({
        content: (
          <>
            <Box p={3}>
              <Grid container alignItems={"center"} flexDirection="column">
                <Grid item>
                  <ErrorIcon color="secondary" sx={{ fontSize: 100 }} />
                </Grid>
                <Grid item>
                  <Typography
                    variant="h4"
                    sx={{ mt: 2 }}
                    align="center"
                    textTransform="uppercase"
                  >
                    A coluna{" "}
                    <strong> {listaCEP.matchCode.selectedColumn} </strong> não
                    possui CEPs válidos.
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body1" sx={{ mt: 2 }} align="center">
                    Por favor, selecione uma coluna com CEPs válidos, ou outro
                    arquivo.
                  </Typography>
                </Grid>
                <Grid item>
                  <Box my={3}>
                    <Button
                      variant="contained"
                      sx={{ mt: 2 }}
                      onClick={() => {
                        closeModal();
                      }}
                    >
                      Ok
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </>
        ),
      });

      return false;
    } else {
      setLoading({
        id: loadingId,
        loading: false,
      });

      return true;
    }
  }, [
    setLoading,
    listaCEP.fileListData.fileListTableValues.fileListValidData,
    listaCEP.fileListData.fileListTableValues.fileListInvalidData,
    listaCEP.matchCode.selectedColumn,
    openModal,
    closeModal,
  ]);

  const openCreateListaCEPForm = useCallback(
    (openForm: boolean) => {
      if (openForm) {
        if (currentUser) {
          dispatch(
            setCreateListaCEPIdUsuarioState({
              idUsuario: currentUser.IdUsuario,
            })
          );
        }
      } else {
        dispatch(resetCreateListaCEPFormState());
      }
      dispatch(setOpenCreateListaCEPState(openForm));
    },
    [dispatch, currentUser]
  );

  const closeEditListaCEPForm = useCallback(() => {
    dispatch(setOpenEditListaCEPState(false));
    dispatch(resetEditListaCEPFormState());
  }, [dispatch]);

  const openImportFileListaCEP = useCallback(
    (openForm: boolean) => {
      dispatch(setOpenImportFileCEPState(openForm));
    },
    [dispatch]
  );

  const openPreviewListaCEP = useCallback(
    (openForm: boolean) => {
      dispatch(setOpenPreviewListaCEPState(openForm));
    },
    [dispatch]
  );

  const openEditCEP = useCallback(
    (openForm: boolean) => {
      dispatch(setOpenEditCEPState(openForm));
    },
    [dispatch]
  );

  const resetListasCEP = useCallback(() => {
    dispatch(resetListasCEPState());
  }, [dispatch]);

  return {
    findCepKeyInArray,
    resetListaCEPFileData,
    selectColumn,
    setFile,
    setCreateListaCEPName,
    setEditListaCEPName,
    resetForm,
    addFileListToListaCEP,
    setEditListaCEPFields,
    selectListaCEPToEdit,
    addItemToCEPList,
    listaCEPEditCEP,
    listaCEPRemoveCEP,
    setupListaCEP,
    createListaCEP,
    updateListaCEP,
    updateListaCEPStatus,
    verifyListaCEPFileData,
    openCreateListaCEPForm,
    closeEditListaCEPForm,
    openImportFileListaCEP,
    openPreviewListaCEP,
    openEditCEP,
    resetListasCEP,
  };
};
